(() => {
  'use strict';

  const ROOT = document.documentElement;
  const annotated = new WeakSet();
  const usedToolNames = new Set();
  let toolsRegistered = false;

  const escapeSelector = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  };

  const nextToolName = (baseName) => {
    let name = baseName;
    let index = 2;
    while (usedToolNames.has(name) || document.querySelector(`[toolname="${escapeSelector(name)}"]`)) {
      name = `${baseName}_${index}`;
      index += 1;
    }
    usedToolNames.add(name);
    return name;
  };

  const setParameterDescription = (form, selector, description) => {
    form.querySelectorAll(selector).forEach((field) => {
      if (!field.hasAttribute('toolparamdescription')) {
        field.setAttribute('toolparamdescription', description);
      }
    });
  };

  const annotateForm = (form, config) => {
    if (!form || annotated.has(form) || form.hasAttribute('toolname')) return;

    const toolName = nextToolName(config.name);
    form.setAttribute('toolname', toolName);
    form.setAttribute('tooldescription', config.description);
    if (config.autoSubmit) form.setAttribute('toolautosubmit', '');

    config.parameters.forEach(({ selector, description }) => {
      setParameterDescription(form, selector, description);
    });

    form.dataset.remWebmcpAnnotated = 'true';
    annotated.add(form);
  };

  const detectAndAnnotateForms = () => {
    document.querySelectorAll('form').forEach((form) => {
      if (annotated.has(form) || form.hasAttribute('toolname')) return;

      const isProductForm = form.matches('[data-type="add-to-cart-form"]');
      const isCartForm = form.id === 'cart' || /\/cart(?:$|[?#])/.test(form.action || '');
      const hasNewsletterEmail = form.querySelector('input[name="contact[email]"]');
      const isNewsletterForm = hasNewsletterEmail && form.querySelector('input[name="contact[tags]"][value*="newsletter"]');
      const isContactForm = hasNewsletterEmail && form.querySelector('textarea[name="contact[body]"]');
      const isSearchForm = form.matches('[role="search"]') || form.querySelector('input[name="q"]');

      if (isProductForm) {
        annotateForm(form, {
          name: 'prepare_add_to_cart',
          description: 'Prepare the selected REM Organics product and quantity for the shopper to review before they manually choose Add to cart. This tool never completes checkout or payment.',
          autoSubmit: false,
          parameters: [
            { selector: 'input[name="id"]', description: 'Currently selected product variant. The store keeps this value in sync with the visible variant selection.' },
            { selector: 'input[name="quantity"]', description: 'Number of packs to prepare for the cart.' }
          ]
        });
        return;
      }

      if (isCartForm) {
        annotateForm(form, {
          name: 'review_or_update_cart',
          description: 'Review the shopping cart or prepare changes to item quantities for the shopper to confirm. This tool does not start checkout or collect payment.',
          autoSubmit: false,
          parameters: [
            { selector: 'input[name="updates[]"]', description: 'Desired quantity for this cart item. Set to zero only when the shopper wants to remove the item.' },
            { selector: 'textarea[name="note"]', description: 'Optional note attached to the cart for the store.' }
          ]
        });
        return;
      }

      if (isContactForm) {
        annotateForm(form, {
          name: 'prepare_contact_request',
          description: 'Fill the REM Organics contact form with an enquiry for the shopper to review and manually send. This tool does not submit the message automatically.',
          autoSubmit: false,
          parameters: [
            { selector: 'input[name="contact[name]"]', description: 'Full name of the person making the enquiry.' },
            { selector: 'input[name="contact[email]"]', description: 'Email address for the REM Organics reply.' },
            { selector: 'input[name="contact[phone]"]', description: 'Optional phone number for follow-up.' },
            { selector: 'select[name="contact[inquiry_type]"]', description: 'Reason for contacting REM Organics, such as wholesale, product information, partnership, or support.' },
            { selector: 'textarea[name="contact[body]"]', description: 'Details of the enquiry for the REM Organics team.' }
          ]
        });
        return;
      }

      if (isNewsletterForm) {
        annotateForm(form, {
          name: 'prepare_newsletter_subscription',
          description: 'Enter an email address in the REM Organics newsletter form for the shopper to review and manually confirm. This tool does not subscribe the shopper automatically.',
          autoSubmit: false,
          parameters: [
            { selector: 'input[name="contact[email]"]', description: 'Email address to receive REM Organics updates and offers.' }
          ]
        });
        return;
      }

      if (isSearchForm) {
        annotateForm(form, {
          name: 'search_rem_organics_catalog',
          description: 'Search the REM Organics catalog for makhana, fox nuts, snack flavours, or related products.',
          autoSubmit: true,
          parameters: [
            { selector: 'input[name="q"]', description: 'Words describing the product, flavour, or snack the shopper wants to find.' }
          ]
        });
      }
    });
  };

  const setFieldValue = (field, value) => {
    if (!field || value === undefined || value === null || value === '') return;
    field.focus({ preventScroll: true });
    field.value = String(value);
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const focusForm = (form) => {
    if (!form) return;
    form.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const firstField = form.querySelector('input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])');
    if (firstField) firstField.focus({ preventScroll: true });
  };

  const registerTool = (definition) => {
    const context = document.modelContext;
    if (!context || typeof context.registerTool !== 'function') return;
    try {
      context.registerTool(definition);
    } catch (error) {
      console.warn('[REM WebMCP] Tool registration skipped:', definition.name, error);
    }
  };

  const registerImperativeTools = () => {
    if (toolsRegistered) return;
    const context = document.modelContext;
    if (!context || typeof context.registerTool !== 'function') return;
    toolsRegistered = true;

    registerTool({
      name: 'search_rem_organics_products',
      description: 'Search the REM Organics catalog for makhana, fox nuts, snack flavours, or related products.',
      inputSchema: {
        type: 'object',
        properties: {
          query: {
            type: 'string',
            minLength: 1,
            description: 'Words describing the product, flavour, or snack to find.'
          }
        },
        required: ['query'],
        additionalProperties: false
      },
      annotations: { readOnlyHint: true },
      execute: async ({ query }) => {
        const cleanQuery = String(query || '').trim();
        if (!cleanQuery) throw new Error('A product search query is required.');
        const destination = new URL('/search', window.location.origin);
        destination.searchParams.set('q', cleanQuery);
        destination.searchParams.set('options[prefix]', 'last');
        window.location.assign(destination.toString());
        return `Opening REM Organics search results for: ${cleanQuery}`;
      }
    });

    registerTool({
      name: 'open_rem_organics_cart',
      description: 'Open the current REM Organics shopping cart so the shopper can review items before checkout.',
      inputSchema: {
        type: 'object',
        properties: {},
        additionalProperties: false
      },
      annotations: { readOnlyHint: true },
      execute: async () => {
        window.location.assign(new URL('/cart', window.location.origin).toString());
        return 'Opening the REM Organics cart for review.';
      }
    });

    registerTool({
      name: 'prepare_current_product_for_cart',
      description: 'Set a quantity on the currently visible product and bring the Add to cart control into view for the shopper to confirm. This tool does not add an item, start checkout, or collect payment.',
      inputSchema: {
        type: 'object',
        properties: {
          quantity: {
            type: 'integer',
            minimum: 1,
            maximum: 99,
            description: 'Number of packs to prepare for the cart.'
          }
        },
        additionalProperties: false
      },
      annotations: { readOnlyHint: false },
      execute: async ({ quantity = 1 }) => {
        const form = document.querySelector('form[data-type="add-to-cart-form"]');
        if (!form) return 'No purchasable product form is available on this page.';
        const quantityField = form.querySelector('input[name="quantity"]');
        setFieldValue(quantityField, Math.max(1, Math.min(99, Number(quantity) || 1)));
        focusForm(form);
        const submit = form.querySelector('button[name="add"]');
        if (submit) submit.focus({ preventScroll: true });
        return 'The product form is ready for review. The shopper must still choose Add to cart.';
      }
    });

    registerTool({
      name: 'prepare_rem_organics_contact_request',
      description: 'Fill the REM Organics contact form with the shopper\'s details for review and manual submission. This tool does not send the enquiry automatically.',
      inputSchema: {
        type: 'object',
        properties: {
          name: { type: 'string', description: 'Full name of the person making the enquiry.' },
          email: { type: 'string', format: 'email', description: 'Email address for the REM Organics reply.' },
          phone: { type: 'string', description: 'Optional phone number for follow-up.' },
          inquiryType: { type: 'string', description: 'Reason for contacting REM Organics.' },
          message: { type: 'string', description: 'Details of the enquiry for the REM Organics team.' }
        },
        required: ['name', 'email', 'message'],
        additionalProperties: false
      },
      annotations: { readOnlyHint: false },
      execute: async ({ name, email, phone, inquiryType, message }) => {
        const form = Array.from(document.querySelectorAll('form')).find((candidate) => candidate.querySelector('textarea[name="contact[body]"]'));
        if (!form) return 'The REM Organics contact form is not available on this page.';
        setFieldValue(form.querySelector('input[name="contact[name]"]'), name);
        setFieldValue(form.querySelector('input[name="contact[email]"]'), email);
        setFieldValue(form.querySelector('input[name="contact[phone]"]'), phone);
        setFieldValue(form.querySelector('select[name="contact[inquiry_type]"]'), inquiryType);
        setFieldValue(form.querySelector('textarea[name="contact[body]"]'), message);
        focusForm(form);
        const submit = form.querySelector('button[type="submit"]');
        if (submit) submit.focus({ preventScroll: true });
        return 'The contact request is filled in and ready for the shopper to review and send.';
      }
    });

    ROOT.dataset.remWebmcpTools = 'registered';
  };

  const start = () => {
    detectAndAnnotateForms();
    registerImperativeTools();

    const observer = new MutationObserver((records) => {
      if (records.some((record) => record.addedNodes.length)) {
        window.requestAnimationFrame(detectAndAnnotateForms);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
